/**
 * tipos.ts — Interfaces do dominio do jogo (cliente).
 *
 * Refletem o que o servidor envia. As versoes "vivas" (com sprite, etc) usam
 * essas interfaces como base e adicionam propriedades de render.
 */

import type { Container, Graphics } from 'pixi.js';

// ---------------------------------------------------------------------------
// Planeta — pode ser pequeno (raio 30), medio (45) ou grande (60)
// ---------------------------------------------------------------------------

export type TamanhoPlaneta = 'pequeno' | 'medio' | 'grande';
export type TipoEspecial = 'industrial' | 'fortaleza' | 'trampolim';

export interface PlanetaServidor {
  id: number;
  x: number;
  y: number;
  tamanho: TamanhoPlaneta;
  raio: number;
  dono_id: number;
  num_naves: number;
  num_torres: number;
  slots_torre: number;
  capacidade: number;
  especial?: TipoEspecial | null;
  constelacao?: number;
}

export interface Planeta {
  id: number;
  x: number;
  y: number;
  tamanho: TamanhoPlaneta;
  raio: number;
  donoId: number;
  naves: number;
  torres: number;
  slotsTorre: number;
  capacidade: number;
  especial: TipoEspecial | null;
  constelacao: number;
  spriteContainer?: Container; // populado em runtime
}

// ---------------------------------------------------------------------------
// Nave — entidade individual com posicao no servidor
// ---------------------------------------------------------------------------

export const ESTADO_ORBITANDO = 0;
export const ESTADO_EM_TRANSITO = 1;
export const ESTADO_ATACANDO_PLANETA = 2;
export const ESTADO_MORTA = 255;

export interface Nave {
  id: number;
  donoId: number;
  estado: number;
  x: number;              // posicao do SERVIDOR (alvo da interpolacao)
  y: number;
  hp: number;
  contextId: number;      // planeta_id (origem) ou alvo
  angulo: number;         // angulo orbital (rad) vindo do servidor; 0 em transito
  rumo: number;           // direcao do nariz (rad), calculada no cliente
  sprite?: Container;     // Fase 6: Sprite do dardo
  rastro?: Container;     // Fase 6: rastro (so em transito)
}

// ---------------------------------------------------------------------------
// Tiro — efemero, vive 150ms
// ---------------------------------------------------------------------------

export interface TiroServidor {
  origem_id: number;
  alvo_id: number;
  origem_tipo: 'nave' | 'torre';
  ox: number;
  oy: number;
  ax: number;
  ay: number;
  cor_dono: number;
}

export interface TiroVivo {
  sprite: Graphics;
  ttlMs: number;
  totalTtlMs: number;
}

// ---------------------------------------------------------------------------
// Config do jogo (vem no partida_inicio)
// ---------------------------------------------------------------------------

export interface ConfigJogo {
  nave_por_torre_segundos: number;
  velocidade_nave: number;
  nave_alcance_tiro: number;
  nave_cooldown_tiro: number;
  nave_dano: number;
  torre_alcance_tiro: number;
  torre_cooldown_tiro: number;
  torre_dano: number;
  nave_velocidade_orbital: number;
  nave_raio_orbital_extra: number;
  custo_torre: number;
  custos_torre?: Record<TamanhoPlaneta, number>;
  tempo_limite_partida: number;
  // ... outros campos opcionais que nao sao acessados diretamente
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Estado do jogo (cliente)
// ---------------------------------------------------------------------------

export interface EstadoJogo {
  suaCor: number;
  meuId: number;
  salaId: string | null;
  planetas: Map<number, Planeta>;
  naves: Map<number, Nave>;
  config: ConfigJogo | null;
  tamanhoMapa: number;
  ticksRecebidos: number;
  navesMortas: number;
  pctEnvio: number;         // Fase 6: fracao do envio rapido (botao direito / teclas 1-4)
  modoMulti: boolean;       // Fase 6: toque acumula planetas (botao "Varios")
  tempoDecorrido: number;   // Fase 6: segundos desde o inicio (relogio do HUD)
}

export function novoEstado(): EstadoJogo {
  return {
    suaCor: 0,
    meuId: 0,
    salaId: null,
    planetas: new Map(),
    naves: new Map(),
    config: null,
    tamanhoMapa: 3000,
    ticksRecebidos: 0,
    navesMortas: 0,
    pctEnvio: 0.5,
    modoMulti: false,
    tempoDecorrido: 0,
  };
}
